import React, { useState } from 'react';
import { X, ExternalLink, ShoppingBag, CreditCard, Banknote, CheckCircle } from 'lucide-react';
import { Product, ColorVariant } from '../data/products';
import { createRazorpayOrder, createCodOrder, verifyRazorpayPayment, getRazorpayConfig, DuplicateCheckResponse } from '../api/payment';
import { useUser } from '../context/UserContext';
import AuthModal from './AuthModal';
import AddressModal from './AddressModal';
import DuplicateOrderModal from './DuplicateOrderModal';
import { useNavigate } from 'react-router-dom';

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  selectedColorVariant?: ColorVariant | null;
  onPurchase: (platform: 'amazon' | 'flipkart') => void;
}

// Ensure the Razorpay object exists on window
declare global {
  interface Window {
    Razorpay: any;
  }
}

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  product,
  selectedColorVariant,
  onPurchase,
}) => {
  const [loadingType, setLoadingType] = useState<'razorpay' | 'cod' | null>(null);
  const loading = loadingType !== null;
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { user, token } = useUser();
  const navigate = useNavigate();

  // Modal states
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Duplicate Order Warning Modal state
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateCheckResponse | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingPurchaseType, setPendingPurchaseType] = useState<'razorpay' | 'cod' | null>(null);

  if (!isOpen) return null;

  const handleRazorpayPurchase = async (confirmDuplicate: boolean = false) => {
    if (!user || !token) {
      setPaymentError(null);
      setShowAuthModal(true);
      return;
    }

    if (!user.address?.street || !user.address?.city || !user.address?.zip) {
      setPaymentError(null);
      setShowAddressModal(true);
      return;
    }

    if (
      !user.phone?.trim() ||
      !user.address?.street?.trim() ||
      !user.address?.city?.trim() ||
      !user.address?.state?.trim() ||
      !user.address?.zip?.trim()
    ) {
      setPaymentError(
        'Shipping address and phone number are required. Please add this item to your cart and proceed to Checkout (where you can fill in your shipping info), or update your details in your Profile first.'
      );
      return;
    }

    try {
      setLoadingType('razorpay');
      setPaymentError(null);

      const shippingAddress = {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '9999999999',
        ...user.address
      };

      // 1. Create order on backend
      const createOrderRes = await createRazorpayOrder([{ 
        productId: product.id.toString(), 
        quantity: 1,
        selectedColor: selectedColorVariant?.name
      }], shippingAddress, token, confirmDuplicate);

      if (!confirmDuplicate && createOrderRes.hasDuplicate) {
        setDuplicateInfo(createOrderRes as DuplicateCheckResponse);
        setPendingPurchaseType('razorpay');
        setShowDuplicateModal(true);
        setLoadingType(null);
        return;
      }

      if (!createOrderRes.success || !createOrderRes.order) {
        setPaymentError('Could not create order on the server.');
        setLoadingType(null);
        return;
      }

      // 2. Load Razorpay script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        setPaymentError('Razorpay SDK failed to load. Are you online?');
        setLoadingType(null);
        return;
      }

      let razorpayKey = 'rzp_test_Rjul01wbs88mD1';
      try {
        const config = await getRazorpayConfig();
        razorpayKey = config.keyId;
      } catch (err) {
        console.warn('Could not fetch Razorpay key from backend, using fallback', err);
      }

      // 3. Initialize Razorpay options
      const options = {
        key: razorpayKey, 
        amount: createOrderRes.order.amount,
        currency: createOrderRes.order.currency,
        name: 'c nine',
        description: product.title,
        order_id: createOrderRes.order.id,
        handler: async function (response: any) {
          try {
            // 4. Verify Payment on backend
            const verifyRes = await verifyRazorpayPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );
            
            if (verifyRes.success && createOrderRes.order) {
              setPaymentSuccess(true);
              onClose();
              navigate(`/order/${createOrderRes.order.id}`);
            } else {
              setPaymentError('Payment verification failed.');
            }
          } catch (err: any) {
            setPaymentError(err.message || 'Payment verification failed.');
          }
        },
        prefill: {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          contact: user.phone || '',
        },
        theme: {
          color: '#dc2626',
        },
      };

      const paymentObject = new window.Razorpay(options);
      
      paymentObject.on('payment.failed', function (response: any) {
        setPaymentError(response.error.description || 'Payment failed.');
      });
      
      paymentObject.open();
    } catch (err: any) {
      setPaymentError(err.message || 'Something went wrong while initiating payment.');
    } finally {
      setLoadingType(null);
    }
  };

  const handleModalClose = () => {
    // Reset state when closing
    setTimeout(() => {
      setPaymentSuccess(false);
      setPaymentError(null);
      setLoadingType(null);
    }, 300);
    onClose();
  };

  const handleCodPurchase = async (confirmDuplicate: boolean = false) => {
    if (!user || !token) {
      setPaymentError(null);
      setShowAuthModal(true);
      return;
    }

    if (!user.address?.street || !user.address?.city || !user.address?.zip) {
      setPaymentError(null);
      setShowAddressModal(true);
      return;
    }

    if (
      !user.phone?.trim() ||
      !user.address?.street?.trim() ||
      !user.address?.city?.trim() ||
      !user.address?.state?.trim() ||
      !user.address?.zip?.trim()
    ) {
      setPaymentError(
        'Shipping address and phone number are required. Please add this item to your cart and proceed to Checkout (where you can fill in your shipping info), or update your details in your Profile first.'
      );
      return;
    }

    try {
      setLoadingType('cod');
      setPaymentError(null);

      const shippingAddress = {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        ...user.address
      };

      const createOrderRes = await createCodOrder([{ 
        productId: product.id.toString(), 
        quantity: 1,
        selectedColor: selectedColorVariant?.name
      }], shippingAddress, token, confirmDuplicate);

      if (!confirmDuplicate && createOrderRes.hasDuplicate) {
        setDuplicateInfo(createOrderRes as DuplicateCheckResponse);
        setPendingPurchaseType('cod');
        setShowDuplicateModal(true);
        setLoadingType(null);
        return;
      }

      if (createOrderRes.success && createOrderRes.order) {
        setPaymentSuccess(true);
        onClose();
        navigate(`/order/${createOrderRes.order.id}`);
      } else {
        setPaymentError(createOrderRes.message || 'Failed to place COD order.');
      }
    } catch (err: any) {
      setPaymentError(err.message || 'Failed to place COD order.');
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleModalClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {paymentSuccess ? 'Order Confirmed' : 'Purchase Options'}
            </h2>
            <button
              onClick={handleModalClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {paymentSuccess ? (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Order Placed!</h3>
                <p className="text-gray-600 mb-6">
                  Thank you for your purchase. Your order for <strong>{product.title}</strong> has been confirmed.
                </p>
                <button
                  onClick={handleModalClose}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <>
                {(() => {
                  const displayImage = selectedColorVariant?.image || product.image;
                  const displayPrice = (selectedColorVariant?.price !== undefined && selectedColorVariant.price > 0) ? selectedColorVariant.price : product.price;

                  return (
                    <div className="text-center mb-4 sm:mb-6">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-50 rounded-lg mx-auto mb-3 sm:mb-4 flex items-center justify-center overflow-hidden border border-gray-200 p-1">
                        {displayImage ? (
                          <img src={displayImage} alt={product.title} className="w-full h-full object-contain rounded" />
                        ) : (
                          <ShoppingBag className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
                        )}
                      </div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 px-2">
                        {product.title}
                      </h3>
                      {selectedColorVariant?.name && (
                        <div className="mb-2">
                          <span className="inline-block text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 rounded-md">
                            Selected Color: {selectedColorVariant.name}
                          </span>
                        </div>
                      )}
                      <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 px-2">
                        Complete your purchase directly or via our partner platforms.
                      </p>
                      <div className="text-xl sm:text-2xl font-bold text-primary-600">₹{displayPrice}</div>
                    </div>
                  );
                })()}

                {paymentError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg text-center font-medium">
                    {paymentError}
                  </div>
                )}

                {/* Platform Options */}
                <div className="space-y-3 sm:space-y-4">
                  {/* Native Razorpay */}
                  <button
                    onClick={() => handleRazorpayPurchase(false)}
                    disabled={loading}
                    className={`w-full bg-primary-600 hover:bg-primary-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold transition-colors flex items-center justify-center text-sm sm:text-base ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center">
                      <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      <span>{loadingType === 'razorpay' ? 'Processing...' : 'Pay Online (Razorpay)'}</span>
                    </div>
                  </button>

                  {/* Cash on Delivery */}
                  <button
                    onClick={() => handleCodPurchase(false)}
                    disabled={loading}
                    className={`w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold transition-colors flex items-center justify-center text-sm sm:text-base ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center">
                      <Banknote className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      <span>{loadingType === 'cod' ? 'Processing...' : 'Cash on Delivery (COD)'}</span>
                    </div>
                  </button>

                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">or</span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>

                  {/* Flipkart */}
                  <button
                    onClick={() => onPurchase('flipkart')}
                    disabled={loading}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold transition-colors flex items-center justify-center group text-sm sm:text-base"
                  >
                    <div className="flex items-center">
                      <span className="text-base sm:text-lg font-bold mr-2 sm:mr-3">Flipkart</span>
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                </div>

                {/* Additional Info */}
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Secure Checkout</h4>
                  <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                    <li>• 100% secure payment processing</li>
                    <li>• Multiple payment methods supported</li>
                    <li>• Instant confirmation</li>
                  </ul>
                </div>

                {/* Cancel Button */}
                <button
                  onClick={handleModalClose}
                  disabled={loading}
                  className="w-full mt-3 sm:mt-4 text-gray-600 hover:text-gray-800 py-2.5 sm:py-2 px-4 rounded-lg font-medium transition-colors text-sm sm:text-base"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          setTimeout(() => {
            handleRazorpayPurchase();
          }, 100);
        }}
      />

      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSuccess={() => {
          setShowAddressModal(false);
          setTimeout(() => {
            handleRazorpayPurchase();
          }, 100);
        }}
      />

      <DuplicateOrderModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        duplicateInfo={duplicateInfo || { success: false, hasDuplicate: false }}
        onProceedAnyway={() => {
          setShowDuplicateModal(false);
          if (pendingPurchaseType === 'cod') {
            handleCodPurchase(true);
          } else {
            handleRazorpayPurchase(true);
          }
        }}
        loading={loading}
      />
    </div>
  );
};

export default PurchaseModal;
